import { inject, Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { map } from 'rxjs/operators';
import { FileAttachment, IFileAttachmentService } from '@advinsoftwares/ngx-rich-text-editor';
import { DocumentMasterService } from 'src/app/Common/DocumentMasterService/document-master.service';
import { DocumentMasterDTO } from 'src/app/Common/DocumentMasterService/documentmaster.interface';
import { UserLoginService } from 'src/app/Common/UserLoginService/userlogin.service';
import { environment } from 'src/environments/environment';

/**
 * Wires the editor's Attach File dialog to the same DocumentMaster_Upload
 * endpoint used by AdvErpImageAssetService (see that file for the fuller
 * rationale) — same TenantId/TenantBranchId/SoftwareId contract, just for
 * arbitrary attached files instead of images.
 *
 * list()/delete() are no-ops for the same reason as the image service:
 * no "list previously uploaded files for this tenant" or delete-by-id
 * endpoint exists yet, and the old CuteEditor integration never offered
 * either capability. Not a regression — just not built yet.
 */
@Injectable()
export class AdvErpFileAttachmentService implements IFileAttachmentService {
  private readonly documentMasterService = inject(DocumentMasterService);
  private readonly userLoginService = inject(UserLoginService);

  list(): Observable<FileAttachment[]> {
    return of([]);
  }

  upload(file: File): Observable<FileAttachment> {
    const user = this.userLoginService.User();
    const formData = new FormData();
    formData.append('File', file, file.name);
    formData.append('TenantId', user.companyId.toString());
    formData.append('TenantBranchId', user.loggedInBranchId.toString());
    formData.append('SoftwareId', environment.SoftwareId);

    return this.documentMasterService
      .DocumentMaster_Upload(formData)
      .pipe(map((response) => this.toFileAttachment(file, response)));
  }

  delete(): Observable<void> {
    return of(void 0);
  }

  private toFileAttachment(file: File, response: DocumentMasterDTO): FileAttachment {
    return {
      id: response.documentId ? response.documentId.toString() : crypto.randomUUID(),
      fileName: response.fileName || file.name,
      url: response.url || '',
      sizeBytes: file.size,
      contentType: file.type || 'application/octet-stream',
      createdAtUtc: response.createdOn
        ? new Date(response.createdOn).toISOString()
        : new Date().toISOString(),
    };
  }
}
